import { Button, Combobox, Input, Item, ItemActions, ItemHeader, ItemTitle, Switch, Textarea } from '@ibsys/kit';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DISPLAY_FORMAT_TYPES, DisplayFormatType } from '@/entities/attributes';
import { useGetAttributeKinds, useGetAttributeTypes, useGetDisplayFormats } from '../../../utils';
import { AttributeFormValues } from '../validation';
import { RuleField } from '../RuleField';

type Props = {
  index: number;
  disabled?: boolean;
  onRemove: () => void;
};

export const StructureAttributeItem: FC<Props> = ({ index, disabled, onRemove }) => {
  const { t } = useTranslation(['attributes', 'common']);
  const { control, watch } = useFormContext<AttributeFormValues>();
  const { options } = useGetAttributeTypes();
  const { options: displayFormatOptions } = useGetDisplayFormats();
  const { options: kindOptions } = useGetAttributeKinds();

  const attribute = watch(`structure.attributes.${index}`);


  const typeOptions = options.map((o) => ({ label: o.label!, value: o.value }));

  // Check if displayFormat should be shown (only for INTEGER and DECIMAL types when creating new attribute)
  const isDisplayFormatSupported =
    attribute?.isNew && attribute?.dataType && DISPLAY_FORMAT_TYPES.includes(attribute.dataType as DisplayFormatType);

  return (
    <Item variant="outline" size="sm" className="w-full">
      <ItemHeader>
        <ItemTitle>{attribute?.isNew ? t('attributes:newAttribute') : attribute?.name}</ItemTitle>
        <ItemActions>
          {!disabled && (
            <Button variant="ghost" color="destructive" size="sm" onClick={onRemove}>
              {t('common:delete')}
            </Button>
          )}
        </ItemActions>
      </ItemHeader>
      <div className="flex flex-col gap-3 p-3 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            control={control}
            name={`structure.attributes.${index}.code`}
            label={t('attributes:code')}
            required={attribute?.isNew}
            disabled={disabled || attribute?.isNew}
          />
          <Input
            control={control}
            name={`structure.attributes.${index}.name`}
            label={t('attributes:name')}
            required={attribute?.isNew}
            disabled={disabled}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:items-baseline-last">
          <Combobox
            control={control}
            name={`structure.attributes.${index}.dataType`}
            label={t('attributes:dataType')}
            required={attribute?.isNew}
            disabled={disabled || !attribute?.isNew}
            options={typeOptions}
          />
          <Switch
            control={control}
            name={`structure.attributes.${index}.isList`}
            label={t('attributes:isList')}
            disabled={disabled || !attribute?.isNew}
          />
        </div>
        {isDisplayFormatSupported && (
          <Combobox
            control={control}
            name={`structure.attributes.${index}.displayFormat`}
            label={t('attributes:displayFormat.title', { defaultValue: 'Display format' })}
            disabled={disabled}
            options={displayFormatOptions}
          />
        )}
        <Combobox
          control={control}
          name={`structure.attributes.${index}.type`}
          label={t('attributes:attributeKind.title')}
          disabled={disabled || !attribute?.isNew}
          options={kindOptions}
        />
        <RuleField
          name={`structure.attributes.${index}.rule`}
          rows={2}
          disabled={disabled || !attribute?.isNew}
          inputClassName="max-h-[150px]"
        />
        <Textarea
          control={control}
          name={`structure.attributes.${index}.description`}
          label={t('attributes:description')}
          rows={2}
          disabled={disabled}
        />
        <Textarea
          control={control}
          name={`structure.attributes.${index}.tooltip`}
          label={t('attributes:tooltip')}
          rows={2}
          disabled={disabled}
        />
      </div>
    </Item>
  );
};
